import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LogoutIcon from '@mui/icons-material/Logout';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import BookIcon from '@mui/icons-material/Book';
import RuleIcon from '@mui/icons-material/Rule';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import { Switch, Tooltip, ClickAwayListener, Popper, Paper, Snackbar, Alert } from '@mui/material';
import { analytics } from '../../../firebaseConfig';
import { logEvent } from 'firebase/analytics';
import logger from '../../../utils/logger';
import { useJournalPageEnabled, useUserApiKeysEnabled } from '../../../context/FeatureFlagContext';
import { useConceptGate } from '../../../context/ConceptGateContext';
import ApiKeySettingsModal from './ApiKeySettingsModal';

const HeaderUserMenu = ({
  isDarkMode,
  onDarkModeChange,
  onLogout,
  onShowRulesHelp,
  onStartTour,
  onToggleJournal,
}) => {
  const journalPageEnabled = useJournalPageEnabled();
  const userApiKeysEnabled = useUserApiKeysEnabled();
  const userInfo = useSelector((state) => state.user.userInfo);
  const { subscriptionStatus } = useConceptGate();
  const [isOpen, setIsOpen] = useState(false);
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const anchorRef = useRef(null);
  const navigate = useNavigate();

  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
    logEvent(analytics, 'header_user_menu_toggled', {
      new_state: !isOpen ? 'open' : 'closed'
    });
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setIsOpen(false);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      anchorRef.current?.focus();
    }
  };

  const handleThemeToggle = () => {
    onDarkModeChange();
    logEvent(analytics, 'theme_toggled_from_header', {
      new_theme: isDarkMode ? 'light' : 'dark',
      previous_theme: isDarkMode ? 'dark' : 'light'
    });
  };

  const handleLogout = async () => {
    try {
      setIsOpen(false);
      await onLogout();
      logEvent(analytics, 'user_logged_out_from_header', {});
      navigate('/');
    } catch (error) {
      logger.error('Logout failed:', error);
    }
  };

  const handleTraderJournalClick = () => {
    setIsOpen(false);
    if (onToggleJournal) {
      onToggleJournal();
    } else {
      navigate('/trader-journal');
    }
    logEvent(analytics, 'trader_journal_opened_from_header', {});
  };

  const handleReferralClick = async () => {
    if (userInfo?.referralCode) {
      const referralLink = `https://nanotradingcopilot.com/waitlist?ref=${userInfo.referralCode}`;
      try {
        await navigator.clipboard.writeText(referralLink);
        setSnackbarOpen(true);
        logEvent(analytics, 'referral_link_copied', {
          source: 'header_user_menu'
        });
        setIsOpen(false);
      } catch (err) {
        logger.error('Failed to copy referral link:', err);
      }
    }
  };

  const handleMenuItemClick = (action, eventName) => {
    setIsOpen(false);
    if (action) {
      action();
      logEvent(analytics, eventName, {});
    }
  };

  return (
    <div className="header-user-menu-container">
      <Tooltip title="Account menu" arrow>
        <button
          ref={anchorRef}
          className="header-user-button"
          onClick={handleToggle}
          aria-label="Open user menu"
          aria-haspopup="true"
          aria-expanded={isOpen}
        >
          <div className="header-user-avatar">
            {userInfo?.picture ? (
              <img src={userInfo.picture} alt="User avatar" />
            ) : (
              <span>{userInfo?.username?.[0]?.toUpperCase() || 'U'}</span>
            )}
          </div>
        </button>
      </Tooltip>

      <Popper
        open={isOpen}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        disablePortal={false}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
        ]}
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            className="header-user-dropdown"
            onKeyDown={handleKeyDown}
            elevation={8}
          >
            {/* User Info Header */}
            <div className="header-user-info">
              <div className="header-user-avatar-large">
                {userInfo?.picture ? (
                  <img src={userInfo.picture} alt="User avatar" />
                ) : (
                  <span>{userInfo?.username?.[0]?.toUpperCase() || 'U'}</span>
                )}
              </div>
              <div className="header-user-details">
                <span className="header-user-name">{userInfo?.username || 'User'}</span>
                <span className="header-user-plan">
                  {subscriptionStatus?.tierName || (userInfo?.premium ? 'Plus' : 'Free')} plan
                </span>
              </div>
            </div>

            {/* Menu Items */}
            <div className="header-user-menu-items">
              {journalPageEnabled && (
                <button
                  className="header-user-menu-item"
                  onClick={handleTraderJournalClick}
                >
                  <BookIcon fontSize="small" />
                  <span>Trader Journal</span>
                </button>
              )}

              <button
                className="header-user-menu-item dark-mode-item"
                onClick={(e) => e.stopPropagation()}
              >
                <DarkModeOutlinedIcon fontSize="small" />
                <span>Dark mode</span>
                <Switch
                  checked={isDarkMode}
                  onChange={handleThemeToggle}
                  size="small"
                  className="header-dark-mode-switch"
                />
              </button>

              <div className="header-user-menu-divider" />

              {onStartTour && (
                <button
                  className="header-user-menu-item"
                  onClick={() => handleMenuItemClick(onStartTour, 'guide_tour_started_from_header')}
                >
                  <PlayCircleOutlineIcon fontSize="small" />
                  <span>Guide</span>
                </button>
              )}

              {onShowRulesHelp && (
                <button
                  className="header-user-menu-item"
                  onClick={() => handleMenuItemClick(onShowRulesHelp, 'validation_rules_opened_from_header')}
                >
                  <RuleIcon fontSize="small" />
                  <span>Validation Rules</span>
                </button>
              )}

              {userInfo?.referralCode && (
                <button
                  className="header-user-menu-item"
                  onClick={handleReferralClick}
                >
                  <PersonAddIcon fontSize="small" />
                  <span>Refer a Friend</span>
                </button>
              )}

              {userApiKeysEnabled && (
                <button
                  className="header-user-menu-item"
                  onClick={() => {
                    setIsOpen(false);
                    setApiKeyModalOpen(true);
                    logEvent(analytics, 'api_keys_opened_from_header', {});
                  }}
                >
                  <VpnKeyOutlinedIcon fontSize="small" />
                  <span>API Keys</span>
                </button>
              )}

              <button
                className="header-user-menu-item"
                onClick={() => {
                  setIsOpen(false);
                  window.open('https://nanotradingcopilot.com/docs/', '_blank');
                  logEvent(analytics, 'help_support_opened_from_header', {});
                }}
              >
                <HelpOutlineIcon fontSize="small" />
                <span>Help & Support</span>
              </button>

              <div className="header-user-menu-divider" />

              <button
                className="header-user-menu-item logout-item"
                onClick={handleLogout}
              >
                <LogoutIcon fontSize="small" />
                <span>Log out</span>
              </button>
            </div>
          </Paper>
        </ClickAwayListener>
      </Popper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Referral link copied!
        </Alert>
      </Snackbar>

      <ApiKeySettingsModal
        isOpen={apiKeyModalOpen}
        onClose={() => setApiKeyModalOpen(false)}
      />
    </div>
  );
};

export default HeaderUserMenu;
